import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaCheck, FaChevronDown, FaChevronUp, FaQuestionCircle, FaPlay } from 'react-icons/fa';
import { useBaldaLogic } from './useBaldaLogic';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard'; // <-- ДОБАВЛЕНО
import './Balda.css';
import '../../ui/ArcadeShared.css';

// --- CONSTANTS (Вынесены за пределы компонента) ---

// Клавиатура (ЙЦУКЕН)
const KEYBOARD_ROWS = [
  ['Й','Ц','У','К','Е','Н','Г','Ш','Щ','З','Х'],
  ['Ф','Ы','В','А','П','Р','О','Л','Д','Ж','Э'],
  [null, 'Я','Ч','С','М','И','Т','Ь','Б','Ю','Ъ',]
];

// Массив всех допустимых букв для физической клавиатуры
const VALID_CHARS = KEYBOARD_ROWS.flat().filter(c => c !== null);

const Balda = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('idle');
  const [tempCellIndex, setTempCellIndex] = useState(null);
  const [tempChar, setTempChar] = useState(null);
  const [selectedPath, setSelectedPath] = useState([]);

  // Refs для оптимизированного и надежного свайпа
  const isDraggingRef = useRef(false);
  const gridRef = useRef(null);
  const cellRectsRef = useRef([]);
  const lastHoveredIndexRef = useRef(null);
  const pathRef = useRef([]); // Синхронное хранилище пути

  const resetTurn = useCallback(() => {
    setPhase('idle');
    setTempCellIndex(null);
    setTempChar(null);
    setSelectedPath([]);
    pathRef.current = []; // Сбрасываем ref пути
  },[]);

  // ФИКС: Передаем функцию очистки поля в хук (очистится только при успехе)
  const logic = useBaldaLogic(socket, { onSuccess: resetTurn });

  const {
    state, duelData, userId, isSpectator, statusMessage, shakeState,
    showResults, setShowResults, finalData, showDebug, lastSyncTime,
    sendAction, triggerShake, showFeedback,
    opponentAction, flashTurn, turnToastKey // <-- берем отсюда
  } = logic;

  const foundWords = useMemo(() => state.foundWords || [], [state.foundWords]);
  const definitionsMap = useMemo(() => state.definitionsMap || {}, [state.definitionsMap]);

  const lastMoveIndex = useMemo(() => {
      const history = state.lastMove || [];
      return history.length > 0 ? history[history.length - 1].newCellIndex : null;
  }, [state.lastMove]);

  const [activeDef, setActiveDef] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const autoShowEnabledRef = useRef(true);
  const hasShownBaseWordRef = useRef(false);

  // ФИКС БАГА №3: Строгое сравнение строк
  const isMyTurn = state.mode !== 'duel' || (duelData && String(duelData.activePlayerId) === String(userId));


  // --- ИСПРАВЛЕНИЕ БАГА №5: Сброс выделения при конце игры ---
  useEffect(() => {
      if (showResults || (duelData && duelData.isFinished)) {
          resetTurn(); // Сбрасываем выделение, если игра кончилась
      }
  }, [showResults, duelData?.isFinished, duelData, resetTurn]);

  const activePlayer = duelData?.players?.find(p => String(p.id) === String(duelData.activePlayerId));
  const activePlayerName = activePlayer?.name || 'Противника';

  // ФИКС: Динамический заголовок и таймер внутри ОДНОГО блока
  let headerTitle = "БАЛДА";
  let displayTimeMs = timeLeft * 1000;

  if (state.mode === 'duel' && duelData) {
      if (duelData.isFinished) {
           const myIdStr = String(userId);
           const winnerIdStr = String(duelData.winnerId);
           if (winnerIdStr === myIdStr) headerTitle = "ПОБЕДА!";
           else headerTitle = "ПОРАЖЕНИЕ";
      } else {
          if (isSpectator) {
              headerTitle = activePlayer ? `СМОТРИТ: ${activePlayerName}` : "КОНЕЦ ИГРЫ";
          } else {
              headerTitle = isMyTurn ? "ВАШ ХОД" : `ХОД: ${activePlayerName}`;
          }
      }

      if (activePlayer && !duelData.isFinished) {
          // Расчет времени: остаток минус время с последнего пакета сервера
          const elapsed = Date.now() - lastSyncTime;
          displayTimeMs = Math.max(0, activePlayer.timeRemaining - elapsed);
      } else if (duelData.isFinished) {
          displayTimeMs = 0; // Если игра окончена, таймер 0
      } else if (activePlayer) {
          displayTimeMs = activePlayer.timeRemaining; // Остановлен
      }
  }

  const mins = Math.floor(displayTimeMs / 60000);
  const secs = Math.floor((displayTimeMs % 60000) / 1000).toString().padStart(2, '0');

  // --- Утилиты для динамического времени и слов ---
  const getPlayerTimeMs = useCallback((p) => {
      if (!p) return 0;
      if (duelData?.isFinished) return p.timeRemaining || 0;
      // Если это активный игрок - динамически отнимаем время
      if (String(p.id) === String(duelData?.activePlayerId)) {
          const elapsed = Date.now() - lastSyncTime;
          return Math.max(0, p.timeRemaining - elapsed);
      }
      return p.timeRemaining;
  }, [duelData, lastSyncTime]); // localTick вызывает пересчет

  const formatTimeMs = (ms) => {
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
      return `${m}:${s}`;
  };


  // --- LOGIC ---

  // Обработчик выбора буквы (клик по виртуальной или нажатие физической)
  const handleKeyClick = useCallback((char) => {
    setTempChar(char);
    setPhase('path');
    window.croco?.haptic?.('impactLight');
  }, []);

  // Автопоказ определения базового слова при старте
  useEffect(() => {
    if (!hasShownBaseWordRef.current && foundWords.length > 0) {
        const baseWord = foundWords[0];
        const def = definitionsMap[baseWord.toLowerCase()];
        if (def) {
            setActiveDef({ word: baseWord.toUpperCase(), text: def });
            hasShownBaseWordRef.current = true;
        }
    }
  }, [foundWords, definitionsMap]);

  // --- Автопоказ определения последнего сыгранного слова ---
  useEffect(() => {
      const history = state.lastMove ||[];
      if (history.length > 0 && autoShowEnabledRef.current) {
          // Берем строго последний ход из массива
          const lastMove = history[history.length - 1];
          const def = definitionsMap[lastMove.word.toLowerCase()];
          if (def) {
              setActiveDef({ word: lastMove.word.toUpperCase(), text: def });
          }
      }
  }, [state.lastMove, definitionsMap]);

  // НОВОЕ: Обработка нажатий физической клавиатуры
  useEffect(() => {
    if (phase !== 'keyboard') return;

    const handleKeyDown = (e) => {
      const key = e.key.toUpperCase();

      // Если нажали Escape - отмена
      if (e.key === 'Escape') {
        resetTurn();
        return;
      }

      // Если нажали букву, которая есть на нашей клавиатуре
      if (VALID_CHARS.includes(key)) {
        handleKeyClick(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, resetTurn, handleKeyClick]);

  // --- DRAWER LOGIC ---

  // --- HANDLERS ---

  // ОБНОВЛЕНО: Прокидываем тип действия, чтобы отличать свайп от клика
  const handleCellAction = (index, type = 'click') => {
    if (showResults || isSpectator) return;

    if (!isMyTurn) {
        if (type === 'click') {
            showFeedback('warning', 'НЕ ВАШ ХОД!');
            triggerShake('neutral');
        }
        return;
    }

    if (phase === 'idle' || phase === 'keyboard') {
      if (type === 'move') return; // Нельзя начать свайп с пустой клетки
      const cell = state.grid[index];
      if (cell !== null) return;

      if (!isAdjacentToExisting(index)) {
         triggerShake('neutral');
         return;
      }

      setTempCellIndex(index);
      setPhase('keyboard');
      window.croco?.haptic?.('selection');
    }
    else if (phase === 'path') {
      processPathSelection(index, type);
    }
  };

  // ОБНОВЛЕНО: Используем pathRef для мгновенного доступа без задержек стейта
  const processPathSelection = (index, type = 'click') => {
      const currentPath = pathRef.current;

      // 1. Отмена последнего (backtracking)
      if (currentPath.length > 0 && index === currentPath[currentPath.length - 1]) {
         if (type === 'click') {
           const newPath = currentPath.slice(0, -1);
           pathRef.current = newPath;
           setSelectedPath(newPath);
           window.croco?.haptic?.('impactLight');
         }
         return;
      }

      // 2. Шаг назад (если вернулись на предпоследнюю при свайпе)
      if (currentPath.length > 1 && index === currentPath[currentPath.length - 2]) {
          const newPath = currentPath.slice(0, -1);
          pathRef.current = newPath;
          setSelectedPath(newPath);
          window.croco?.haptic?.('impactLight');
          return;
      }

      // 3. Нельзя пересекать уже выбранные
      if (currentPath.includes(index)) return;

      // 4. Валидация
      if (currentPath.length === 0) {
         if (!isValidCellForPath(index)) {
             if (type === 'click') triggerShake('neutral');
             return;
         }
         pathRef.current = [index];
         setSelectedPath([index]);
         window.croco?.haptic?.('selection');
      } else {
         const lastIdx = currentPath[currentPath.length - 1];
         if (isAdjacent(lastIdx, index) && isValidCellForPath(index)) {
            const newPath = [...currentPath, index];
            pathRef.current = newPath;
            setSelectedPath(newPath);
            window.croco?.haptic?.('selection');
         } else {
            if (type === 'click') triggerShake('neutral');
         }
      }
  };

  // --- УЛУЧШЕННЫЕ POINTER EVENTS ---
  const handlePointerDown = (e, index) => {
      e.target.setPointerCapture(e.pointerId);
      isDraggingRef.current = true;
      lastHoveredIndexRef.current = index;

      // Кэшируем физические координаты ячеек сетки для молниеносного поиска при свайпе
      if (gridRef.current) {
          cellRectsRef.current = Array.from(gridRef.current.children).map(child => {
              const rect = child.getBoundingClientRect();
              return {
                  index: parseInt(child.dataset.index, 10),
                  left: rect.left,
                  right: rect.right,
                  top: rect.top,
                  bottom: rect.bottom
              };
          });
      }

      handleCellAction(index, 'click');
  };

  const handlePointerMove = (e) => {
      if (!isDraggingRef.current || phase !== 'path') return;

      const x = e.clientX;
      const y = e.clientY;

      // Находим ячейку математически по кэшу (избавляет от лагов document.elementFromPoint)
      const cell = cellRectsRef.current.find(c => x >= c.left && x <= c.right && y >= c.top && y <= c.bottom);

      if (cell && cell.index !== lastHoveredIndexRef.current) {
          lastHoveredIndexRef.current = cell.index;
          handleCellAction(cell.index, 'move');
      }
  };

  const handlePointerUp = (e) => {
      isDraggingRef.current = false;
      lastHoveredIndexRef.current = null;
  };

  const handleSubmit = () => {
    if (phase !== 'path' || selectedPath.length < 2) return;

    if (!selectedPath.includes(tempCellIndex)) {
        showFeedback('error', t('arcade.balda_use_new_letter', 'ИСПОЛЬЗУЙТЕ НОВУЮ БУКВУ'));
        triggerShake('error');
        return;
    }

    const word = currentWord;
    sendAction('submit', { newCell: { index: tempCellIndex, char: tempChar }, path: selectedPath, word: word });
    // ФИКС ОЧИСТКИ: Убрали resetTurn(). Теперь поле очистится только при успехе.
  };

  // --- HELPERS ---

  const isAdjacent = (idx1, idx2) => {
    const w = state.gridSize.cols;
    const r1 = Math.floor(idx1 / w), c1 = idx1 % w;
    const r2 = Math.floor(idx2 / w), c2 = idx2 % w;
    return Math.abs(r1 - r2) + Math.abs(c1 - c2) === 1;
  };

  const isAdjacentToExisting = (index) => {
    const w = state.gridSize.cols;
    const r = Math.floor(index / w);
    const c = index % w;
    const neighbors = [];
    if (r > 0) neighbors.push((r - 1) * w + c);
    if (r < state.gridSize.rows - 1) neighbors.push((r + 1) * w + c);
    if (c > 0) neighbors.push(r * w + (c - 1));
    if (c < state.gridSize.cols - 1) neighbors.push(r * w + (c + 1));

    return neighbors.some(nIdx => state.grid[nIdx] !== null);
  };

  const isValidCellForPath = (index) => {
    return state.grid[index] !== null || index === tempCellIndex;
  };

  // Вычисляем отображаемое слово
  const currentWord = selectedPath.map(idx => {
      if (idx === tempCellIndex) return tempChar;
      return state.grid[idx]?.char;
  }).join('');

  // --- ТЕКСТ В ПОЛЕ ВВОДА ---
  const getInfoText = () => {
      // Приоритет 1: Игра окончена (Баг №2)
      if (state.mode === 'duel' && duelData?.isFinished) {
           const amIWinner = String(duelData.winnerId) === String(userId);
           return amIWinner ? "ПОБЕДА!" : "ИГРА ОКОНЧЕНА";
      }

      // Приоритет 2: Ход соперника (показываем его слово)
      if (opponentAction) return opponentAction.word;

      // Приоритет 3: Мы собираем слово
      if (currentWord) return currentWord;

      // Приоритет 4: Статусы
      if (isSpectator) return `НАБЛЮДЕНИЕ ЗА: ${activePlayerName}`;
      if (!isMyTurn) return `ОЖИДАНИЕ: ${activePlayerName}`;

      // Приоритет 5: Подсказки
      switch(phase) {
          case 'idle': return t('arcade.balda_hint_idle', 'Выберите пустую клетку');
          case 'keyboard': return t('arcade.balda_hint_keyboard', 'Выберите букву');
          case 'path': return t('arcade.balda_hint_path', 'Выделите слово');
          default: return '';
      }
  };

  // --- RENDER ---

  const renderResults = () => {
      if (!finalData || !showResults) return null;

      const myStats = finalData.my || {};

      // ИСПРАВЛЕНИЕ: Высчитываем, заполнена ли вся доска
      const isGridFull = state.grid && state.grid.every(c => c !== null);
      const resultCause = finalData.result || finalData.reason;
      const isFull = resultCause === 'grid_full' || isGridFull;
      const isWinner = state.mode === 'duel' && duelData?.winnerId === userId;

      let title = t('arcade.game_over');
      let stampText = t('arcade.time_up');
      let stampClass = 'stamp-fail';

      if (state.mode === 'duel') {
          title = "ДУЭЛЬ ОКОНЧЕНА";
          stampText = isWinner ? "ПОБЕДА" : "ПОРАЖЕНИЕ";
          stampClass = isWinner ? 'stamp-ok' : 'stamp-fail';
      } else if (isFull) {
          title = t('arcade.balda_win_title', 'ПОЛЕ ЗАПОЛНЕНО!');
          stampText = t('arcade.balda_stamp_full', 'ГЕНИЙ');
          stampClass = 'stamp-ok';
      }

      return (
        <div className="tr-report-overlay">
           <div className="tr-report-card">
               <div className="tr-watermark">BALDA</div>

               <div className="tr-report-header">
                  <div className="tr-report-title">{title}</div>
               </div>

               <div className="tr-stats-grid">
                   <div className="tr-stat-row">
                       <span className="tr-stat-label">{t('arcade.cs_stat_score')}</span>
                       <span className="tr-stat-value">{myStats.score || state.score || 0}</span>
                   </div>
                   {state.mode === 'duel' && (
                       <div className="tr-stat-row">
                           <span className="tr-stat-label">ОСТАЛОСЬ ВРЕМЕНИ</span>
                           <span className="tr-stat-value">
                               {Math.floor((duelData?.players?.find(p => String(p.id) === String(userId))?.timeRemaining || 0) / 60000)}:
                               {Math.floor(((duelData?.players?.find(p => String(p.id) === String(userId))?.timeRemaining || 0) % 60000) / 1000).toString().padStart(2, '0')}
                           </span>
                       </div>
                   )}
                   <div className="tr-stat-row">
                       <span className="tr-stat-label">{t('arcade.wordduel_rank')}</span>
                       <span className="tr-stat-value">{t(`arcade.${myStats.rank || 'balda_rank_student'}`)}</span>
                   </div>
               </div>

               <div className={`tr-result-stamp ${stampClass}`}>
                   {stampText}
               </div>

               {/* ДОБАВЛЕН ЛИДЕРБОРД */}
               {finalData.leaderboard && finalData.leaderboard.length > 0 && (
                   <div style={{width: '100%', marginBottom: '15px'}}>
                       <UnifiedLeaderboard
                          results={finalData.leaderboard}
                          myId={userId}
                          t={t}
                       />
                   </div>
               )}

               <div className="tr-btn-group">
                   {/* Кнопка ОСТАТЬСЯ переведет игру в песочницу, если время кончилось */}
                   <button className="tr-btn tr-btn-secondary" onClick={() => setShowResults(false)}>
                       <FaPlay style={{marginRight: '8px'}} /> {t('arcade.stay', 'ОСТАТЬСЯ')}
                   </button>

                   <button className="tr-btn tr-btn-primary" onClick={onClose}>
                       <FaTimes style={{marginRight: '8px'}} /> {t('arcade.close')}
                   </button>
               </div>
           </div>
        </div>
      );
  };

  // --- Динамический Рендер Хедера ---
  const renderHeader = () => {
      if (state.mode === 'duel' && duelData?.players) {
          const players = duelData.players;

          if (players.length === 2) {
              // РЕЖИМ 2 ИГРОКА (Две половины)
              const mePlaying = players.find(p => String(p.id) === String(userId));
              const p1 = mePlaying || players[0];
              const p2 = mePlaying ? players.find(p => String(p.id) !== String(userId)) : players[1];

              const p1Active = String(p1.id) === String(duelData.activePlayerId) && !duelData.isFinished;
              const p2Active = String(p2.id) === String(duelData.activePlayerId) && !duelData.isFinished;

              return (
                  // ДОБАВЛЕН КЛАСС split-mode
                  <div className="balda-header-duel-inline split-mode">
                      <div className={`duel-inline-player left ${p1Active ? 'active' : ''}`}>
                          <span className="dp-name">{p1.name}</span>
                          <span className="dp-score">⭐{p1.score}</span>
                          <span className="dp-time">{formatTimeMs(getPlayerTimeMs(p1))}</span>
                      </div>
                      <div className={`duel-inline-player right ${p2Active ? 'active' : ''}`}>
                          <span className="dp-name">{p2.name}</span>
                          <span className="dp-score">⭐{p2.score}</span>
                          <span className="dp-time">{formatTimeMs(getPlayerTimeMs(p2))}</span>
                      </div>
                  </div>
              );
          } else {
              // РЕЖИМ > 2 ИГРОКОВ (Себя + место)
              const mePlaying = players.find(p => String(p.id) === String(userId));
              if (!mePlaying) return <div className="balda-title">НАБЛЮДЕНИЕ ЗА ИГРОЙ</div>;

              // Копируем массив перед сортировкой, чтобы не мутировать стейт
              const sorted = [...players].sort((a,b) => b.score - a.score);
              const myRank = sorted.findIndex(p => String(p.id) === String(userId)) + 1;
              const pActive = String(mePlaying.id) === String(duelData.activePlayerId) && !duelData.isFinished;

              return (
                  // ДОБАВЛЕН КЛАСС full-active (если сейчас мой ход)
                  <div className={`balda-header-duel-inline ${pActive ? 'full-active' : ''}`}>
                      <div className={`duel-inline-player left ${pActive ? 'active' : ''}`}>
                          <span className="dp-name">{mePlaying.name}</span>
                          <span className="dp-score">⭐{mePlaying.score}</span>
                          <span className="dp-time">{formatTimeMs(getPlayerTimeMs(mePlaying))}</span>
                      </div>
                      <div className="duel-inline-player right">
                          <span className="dp-rank">— {myRank} место</span>
                      </div>
                  </div>
              );
          }
      }

      // ФОЛЛБЭК: Оригинальный вид для SOLO и PARALLEL
      return (
          <>
              <div className="balda-title" style={{ color: isMyTurn && state.mode === 'duel' ? '#22c55e' : '#fbbf24', fontSize: '16px' }}>
                  {headerTitle}
              </div>
              <div className="balda-stats">
                  <span>{state.score} ⭐️</span>
                  <span style={{ fontFamily: 'monospace', color: isMyTurn ? '#22c55e' : '#cbd5e1', width: '45px', textAlign: 'right' }}>
                      {mins}:{secs}
                  </span>
              </div>
          </>
      );
  };

  // Формируем классы тряски
  const shakeClass = shakeState ? `shake shake-${shakeState}` : '';


  return (
    <div className="balda-board">
        {/* --- ПОЛНОЦЕННАЯ ДЕБАГ ПАНЕЛЬ --- */}
        {showDebug && (
            <div style={{ position: 'absolute', top: 50, left: 10, right: 10, background: 'rgba(0,0,0,0.95)', color: '#0f0', padding: 15, zIndex: 9999, fontSize: 11, overflow: 'auto', maxHeight: '70vh', pointerEvents: 'none', border: '2px solid #0f0', borderRadius: '8px', textAlign: 'left', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                <b style={{color: '#fff', fontSize: '14px', marginBottom: '10px', display: 'block'}}>🐞 DEBUG INFO:</b>
                <pre style={{margin: 0}}>{JSON.stringify({
                    myId: userId,
                    mode: state.mode,
                    isMyTurn,
                    isSpectator,
                    activePlayerId: duelData?.activePlayerId,
                    activePlayerName,
                    players: duelData?.players,
                    isFinished: duelData?.isFinished
                }, null, 2)}</pre>
            </div>
        )}

        {/* ИСПРАВЛЕНИЕ БАГА №3: Показываем тост "ВАШ ХОД!" */}
        {turnToastKey > 0 && (
            <div key={turnToastKey} className="balda-turn-toast">
                ВАШ ХОД!
            </div>
        )}

        {/* Рендерим результаты поверх всего */}
        {renderResults()}



        {/* ВЕРХНЯЯ ШТОРКА */}
        <div className={`balda-drawer ${isDrawerOpen ? 'open' : ''}`}>
             <div className="balda-drawer-content">
                  {activeDef && (
                      <div className="balda-definition-panel" onClick={() => { setActiveDef(null); autoShowEnabledRef.current = false; }}>
                          <span className="balda-def-word">{activeDef.word}</span>
                          <div className="balda-def-text">{activeDef.text}</div>
                      </div>
                  )}

                  {/* НОВАЯ ЛОГИКА РЕНДЕРА СЛОВ В ШТОРКЕ */}
                  {state.mode === 'duel' && duelData?.players ? (
                      <div className="balda-drawer-duel-lists">
                          {duelData.players.map(p => {
                              const pWords = (state.lastMove ||[])
                                  .filter(m => String(m.userId) === String(p.id))
                                  .map(m => m.word);

                              return (
                                  <div key={p.id} className="balda-drawer-player-section">
                                      {/* ЗАДАНИЕ 1: Убрали тире "—", чтобы Flexbox сам аккуратно распределил отступы */}
                                      <div className="balda-drawer-player-header">
                                          <span className="dp-name">{p.name}</span>
                                          <span className="dp-score">{p.score} ⭐️</span>
                                          <span className="dp-count">({pWords.length} слов)</span>
                                          <span className="dp-time">{formatTimeMs(getPlayerTimeMs(p))}</span>
                                      </div>
                                      <div className="balda-words-cloud">
                                          {pWords.length === 0 ? <span className="dp-empty">Пока нет слов</span> : pWords.map((word, i) => {
                                              const hasDef = !!definitionsMap?.[word.toLowerCase()];
                                              return (
                                                  <div
                                                      key={i}
                                                      className={`balda-word-tag ${hasDef ? 'has-def' : ''}`}
                                                      onClick={() => {
                                                          const def = definitionsMap[word.toLowerCase()];
                                                          if (def) {
                                                              setActiveDef({ word: word.toUpperCase(), text: def });
                                                              autoShowEnabledRef.current = true;
                                                              window.croco?.haptic?.('selection');
                                                          }
                                                      }}
                                                  >
                                                      {word.toUpperCase()}
                                                      {hasDef && <FaQuestionCircle className="balda-word-help-icon"/>}
                                                  </div>
                                              );
                                          })}
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  ) : (
                      // Оригинальный рендер для соло/параллельно
                      foundWords.length === 0 ? (
                          <div className="balda-empty-list">{t('arcade.slovodel_start_hint', 'Слова появятся здесь')}</div>
                      ) : (
                          <div className="balda-words-cloud">
                              {foundWords.map((word, i) => {
                                  const hasDef = !!definitionsMap?.[word.toLowerCase()];
                                  return (
                                      <div key={i} className={`balda-word-tag ${hasDef ? 'has-def' : ''}`}
                                          onClick={() => {
                                              const def = definitionsMap[word.toLowerCase()];
                                              if (def) {
                                                  setActiveDef({ word: word.toUpperCase(), text: def });
                                                  autoShowEnabledRef.current = true;
                                                  window.croco?.haptic?.('selection');
                                              }
                                          }}>
                                          {word.toUpperCase()}
                                          {hasDef && <FaQuestionCircle className="balda-word-help-icon"/>}
                                      </div>
                                  );
                              })}
                          </div>
                      )
                  )}
             </div>

             <div className="balda-drawer-footer">
                 <button className="balda-close-drawer-btn" onClick={() => setIsDrawerOpen(false)}>
                     <FaChevronUp />
                 </button>
             </div>
        </div>

        {/* ЕДИНЫЙ ХЕДЕР ДЛЯ ВСЕГО С ОТКРЫТИЕМ/ЗАКРЫТИЕМ ШТОРКИ */}
        <div className="balda-header" onClick={() => setIsDrawerOpen(prev => !prev)}>
            {renderHeader()}
        </div>

        {/* ЗАДАНИЕ 3: КНОПКА ОТКРЫТИЯ/ЗАКРЫТИЯ ШТОРКИ (ЯЗЫЧОК) */}
        {/* Теперь по клику он не только открывает, но и закрывает шторку (toggle) */}
        <div className="balda-drawer-trigger" onClick={() => setIsDrawerOpen(prev => !prev)}>
            <div className="balda-drawer-btn-small">
                 {/* Вращаем иконку в зависимости от того, открыта шторка или нет */}
                 <FaChevronDown style={{ transform: isDrawerOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} />
            </div>
        </div>

        {/* INPUT DISPLAY */}
        <div className="balda-input-wrapper">
            <div className={`balda-input
                ${!currentWord && !opponentAction && !duelData?.isFinished ? 'is-hint' : ''}
                ${opponentAction ? 'opponent-word' : ''}
                ${duelData?.isFinished ? (String(duelData.winnerId) === String(userId) ? 'status-win' : 'status-lose') : ''}
            `}>
                {getInfoText()}
            </div>
        </div>

        {/* GRID */}
        <div className="balda-grid-wrapper">
            <div
                className={`balda-grid ${shakeClass} ${flashTurn ? 'my-turn-flash' : ''}`}
                ref={gridRef}
                style={{ gridTemplateColumns: `repeat(${state.gridSize.cols}, 1fr)` }}
            >
                {Array.from({ length: state.gridSize.rows * state.gridSize.cols }).map((_, idx) => {
                    let char = state.grid[idx]?.char;
                    let isTemp = false;

                    if (idx === tempCellIndex && tempChar) {
                        char = tempChar;
                        isTemp = true;
                    }

                    const isSelected = phase === 'keyboard' && idx === tempCellIndex;
                    const isOnPath = selectedPath.includes(idx);
                    const isHead = selectedPath.length > 0 && selectedPath[selectedPath.length - 1] === idx;
                    const isLastMove = idx === lastMoveIndex;
                    // Добавляем проверку на null для opponentAction
                    const isOpponentPath = opponentAction && opponentAction.path && opponentAction.path.includes(idx);

                    const isPlayable = !char && isAdjacentToExisting(idx);
                    const extraClass = char ? '' : (isPlayable ? 'empty' : 'hidden');

                    return (
                        <div
                            key={idx}
                            data-index={idx}
                            className={`balda-cell
                                ${extraClass}
                                ${isTemp ? 'temp' : ''}
                                ${isSelected ? 'selected' : ''}
                                ${isOnPath ? 'path' : ''}
                                ${isHead ? 'head' : ''}
                                ${isLastMove ? 'last-move' : ''}
                                ${isOpponentPath ? 'opponent-path' : ''}
                            `}
                            onPointerDown={(e) => handlePointerDown(e, idx)}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                        >
                            {char}
                        </div>
                    );
                })}
            </div>
        </div>

        {/* BOTTOM CONTROLS OR KEYBOARD */}
        {isMyTurn && (
            <div className="balda-bottom-area">
                {phase === 'keyboard' && (
                    <div className="balda-keyboard">
                        {KEYBOARD_ROWS.map((row, rIdx) => (
                            <div key={rIdx} style={{display:'flex', width:'100%', justifyContent:'center', gap:'3px', marginBottom:'3px'}}>
                                {row.map(char => (
                                    <button
                                        key={char}
                                        className={`kb-key ${char ? '' : 'empty'}`}
                                        onClick={() => char && handleKeyClick(char)}
                                    >
                                        {char}
                                    </button>
                                ))}
                            </div>
                        ))}
                        <button className="balda-btn btn-cancel" style={{marginTop:'5px'}} onClick={resetTurn}>
                            <FaTimes style={{marginRight: '8px'}} /> {t('arcade.cancel')}
                        </button>
                    </div>
                )}

                {(phase === 'idle' || phase === 'path') && (
                    <div className="balda-controls">
                        <button
                            className="balda-btn btn-cancel"
                            disabled={phase === 'idle'}
                            onClick={resetTurn}
                            style={{opacity: phase === 'idle' ? 0 : 1}}
                        >
                             <FaTimes style={{marginRight: '8px'}} /> {t('arcade.cancel')}
                        </button>

                        <button
                            className="balda-btn btn-submit"
                            disabled={phase !== 'path' || selectedPath.length < 2 || !selectedPath.includes(tempCellIndex)}
                            onClick={handleSubmit}
                        >
                            {phase === 'path' ? (
                                <><FaCheck style={{marginRight:'8px'}} /> {t('arcade.slovodel_send')}</>
                            ) : (
                                <span style={{opacity:0.5}}>{t('arcade.slovodel_send')}</span>
                            )}
                        </button>
                    </div>
                )}
            </div>
        )}

        {isSpectator && !showResults && (
            <div className="balda-bottom-area" style={{ alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                <button className="balda-btn btn-cancel" onClick={onClose} style={{marginTop: '10px', height: '40px', width: '80%'}}>ВЫЙТИ ИЗ ИГРЫ</button>
            </div>
        )}

        {/* STATUS MESSAGE */}
        {statusMessage && (
            <div className="balda-stamp-overlay">
                <div className={`balda-stamp ${statusMessage.type === 'success' ? 'stamp-ok' : statusMessage.type === 'warning' ? 'stamp-warning' : 'stamp-fail'}`}>
                    {statusMessage.text}
                </div>
            </div>
        )}
    </div>
  );
};

export default Balda;